import { MANUFACTURERS, type Chassis, type Manufacturer, type Role } from "@zend/content";

export type SquadSlot = "commander" | "fighter" | "support";

export interface SquadMember {
  unitId: string;
  mfr: Manufacturer;
  role: Role;
  chassis: Chassis;
  slot: SquadSlot;
}

export type LinkKind = "strong" | "commander" | "rival";
export type Doctrine = "column" | "balanced" | "uplink";

export interface UnitChemistry {
  points: number;
  pct: number;
  links: { to: string; kind: LinkKind }[];
}

export interface ChemistryResult {
  perUnit: Record<string, UnitChemistry>;
  totalPoints: number;
  /** 0..100, total points over the maximum of 21. */
  percent: number;
  doctrines: Doctrine[];
}

export const CHEMISTRY_PCT_BY_POINTS = [0, 2, 4, 7] as const;
export const MAX_POINTS_PER_UNIT = 3;
export const MAX_SQUAD_POINTS = 7 * MAX_POINTS_PER_UNIT;

/** FIFA-style chemistry (30-units.md R25). */
export function evaluateChemistry(members: SquadMember[]): ChemistryResult {
  const commander = members.find((m) => m.slot === "commander") ?? null;
  const perUnit: Record<string, UnitChemistry> = {};
  let totalPoints = 0;
  for (const m of members) {
    const links: { to: string; kind: LinkKind }[] = [];
    let same = 0;
    let rivals = 0;
    for (const o of members) {
      if (o === m) continue;
      if (o.mfr === m.mfr) {
        same++;
        links.push({ to: o.unitId, kind: "strong" });
      } else if (MANUFACTURERS[m.mfr].rival === o.mfr) {
        rivals++;
        links.push({ to: o.unitId, kind: "rival" });
      }
    }
    let points = Math.min(2, same);
    if (commander && m !== commander && commander.mfr === m.mfr) {
      points += 1;
      links.push({ to: commander.unitId, kind: "commander" });
    }
    if (rivals > 0) points -= 1;
    points = Math.max(0, Math.min(MAX_POINTS_PER_UNIT, points));
    perUnit[m.unitId] = { points, pct: CHEMISTRY_PCT_BY_POINTS[points] ?? 0, links };
    totalPoints += points;
  }
  const fighters = members.filter((m) => m.slot === "fighter");
  const supports = members.filter((m) => m.slot === "support");
  const doctrines: Doctrine[] = [];
  const chassisCounts = new Map<Chassis, number>();
  for (const f of fighters) chassisCounts.set(f.chassis, (chassisCounts.get(f.chassis) ?? 0) + 1);
  if ([...chassisCounts.values()].some((c) => c >= 3)) doctrines.push("column");
  const roles = new Set(fighters.map((f) => f.role));
  if (roles.has("defender") && roles.has("attacker") && roles.has("saboteur")) doctrines.push("balanced");
  if (supports.length === 2 && supports[0]!.mfr === supports[1]!.mfr) doctrines.push("uplink");
  return { perUnit, totalPoints, percent: Math.round((totalPoints / MAX_SQUAD_POINTS) * 100), doctrines };
}

/** Squad-wide stat effects of doctrines, applied on top of per-unit chemistry. */
export function doctrineBonuses(doctrines: Doctrine[]): { hpPct: number; atkPct: number; auraPct: number } {
  return {
    hpPct: doctrines.includes("column") ? 5 : 0,
    atkPct: doctrines.includes("balanced") ? 3 : 0,
    auraPct: doctrines.includes("uplink") ? 10 : 0,
  };
}
